import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShoppingBag, Menu, X, Instagram, Youtube, User, AlertCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CartDrawer } from "@/components/CartDrawer";
import { StickyBuy } from "@/components/StickyBuy";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BRAND } from "@/lib/api";
import { NAV } from "@/constants/testIds";

export default function Layout() {
  const { t } = useTranslation();
  const { count, setOpen, salesSuspended } = useCart();
  const { customer } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobile(false); }, [location.pathname]);

  const links = [
    { to: "/who-we-are", label: t("nav.who_we_are"), testid: "nav-who-we-are" },
    { to: "/preshhgo", label: t("nav.preshhgo"), testid: NAV.home },
    { to: "/how-it-works", label: t("nav.how_it_works"), testid: NAV.howItWorks },
    { to: "/shop", label: t("nav.shop"), testid: NAV.shop },
    { to: "/professional", label: t("nav.professional"), testid: NAV.professional },
    { to: "/support", label: t("nav.support"), testid: NAV.support },
    { to: "/blog", label: "Blog", testid: "nav-blog" },
    { to: "/contact", label: t("nav.contact"), testid: NAV.contact },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-[#00B5D8] focus:text-black focus:font-bold">
        Skip to content
      </a>
      <header className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] focus-visible:ring-offset-2 focus-visible:ring-offset-black">
            <img src={BRAND.logo} alt="Preshh — home" className="h-8 md:h-9" />
          </Link>
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={l.testid}
                className={({ isActive }) => `preshh-nav-link rounded px-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] focus-visible:ring-offset-2 focus-visible:ring-offset-black ${isActive ? "text-[#00B5D8]" : ""}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/preshhgo" data-testid={NAV.buyNow}
              className="hidden md:inline-flex preshh-btn-primary text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] focus-visible:ring-offset-2 focus-visible:ring-offset-black">
              {t("nav.buy")}
            </Link>
            <Link to={customer ? "/account" : "/login"} data-testid="nav-account"
              className="p-2 rounded-full hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8]" aria-label={customer ? customer.name : t("nav.account")} title={customer ? customer.name : t("nav.account")}>
              <User size={22} strokeWidth={1.5} aria-hidden="true" />
            </Link>
            <button data-testid={NAV.cartButton} onClick={() => setOpen(true)}
              aria-label={`${t("nav.cart") || "Cart"}${count > 0 ? ` (${count})` : ""}`}
              className="relative p-2 rounded-full hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8]">
              <ShoppingBag size={22} strokeWidth={1.5} aria-hidden="true" />
              {count > 0 && (
                <span data-testid={NAV.cartCount} aria-hidden="true"
                  className="absolute -top-1 -right-1 min-w-5 h-5 px-1.5 rounded-full bg-[#00B5D8] text-[#050505] text-[11px] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button data-testid={NAV.mobileMenuToggle} onClick={() => setMobile((s) => !s)}
              aria-expanded={mobile} aria-controls="mobile-nav" aria-label={mobile ? "Close menu" : "Open menu"}
              className="lg:hidden p-2 rounded-full hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8]">
              {mobile ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
            </button>
          </div>
        </div>
        {mobile && (
          <div id="mobile-nav" className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
            <nav aria-label="Mobile" className="px-6 py-8 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={`m-${l.testid}`}
                  className={({ isActive }) => `preshh-nav-link-mobile rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00B5D8] ${isActive ? "text-[#00B5D8]" : ""}`}>
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main id="main-content" className="pt-20">
        {salesSuspended && (
          <div data-testid="sales-paused-banner" className="bg-[#00B5D8] text-black">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-2.5 flex items-center justify-center gap-2 text-center text-sm font-semibold">
              <AlertCircle size={16} className="shrink-0" />
              <span>{t("sales_paused_title")} — {t("sales_paused_body")}</span>
            </div>
          </div>
        )}
        <Outlet />
      </main>

      {!salesSuspended && <StickyBuy />}
      <CartDrawer />

      <footer className="bg-[#0a0a0a] border-t border-white/10 pt-16 pb-8 mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <img src={BRAND.logo} alt="Preshh" className="h-8 mb-4" />
            <p className="text-white/60 text-sm max-w-xs">{t("footer.tagline")}</p>
            <p className="text-white/70 text-xs mt-3">
              <a href="mailto:support@preshh.com.br" className="hover:text-[#00B5D8] transition-colors">support@preshh.com.br</a>
            </p>
            <div className="flex gap-3 mt-4">
              <a href="https://instagram.com/preshh.pt" target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/5 hover:bg-white/10"><Instagram size={16} /></a>
              <a href="https://youtube.com" target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/5 hover:bg-white/10"><Youtube size={16} /></a>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-bold mb-4 tracking-wide">{t("footer.shop")}</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link to="/who-we-are" className="hover:text-white">{t("nav.who_we_are")}</Link></li>
              <li><Link to="/preshhgo" className="hover:text-white">{t("nav.preshhgo")}</Link></li>
              <li><Link to="/shop" className="hover:text-white">{t("nav.shop")}</Link></li>
              <li><Link to="/arts" className="hover:text-white">Preshh Arts</Link></li>
              <li><Link to="/professional" className="hover:text-white">{t("nav.professional")}</Link></li>
              <li><Link to="/quiz" className="hover:text-white">Which Preshh?</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-bold mb-4 tracking-wide">{t("footer.support")}</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link to="/support" className="hover:text-white">{t("nav.support")}</Link></li>
              <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
              <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-white">{t("nav.contact")}</Link></li>
              <li><Link to="/how-it-works" className="hover:text-white">{t("nav.how_it_works")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-bold mb-4 tracking-wide">{t("footer.legal")}</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link to="/legal/privacy" className="hover:text-white">{t("footer.privacy")}</Link></li>
              <li><Link to="/legal/terms" className="hover:text-white">{t("footer.terms")}</Link></li>
              <li><Link to="/legal/shipping" className="hover:text-white">{t("footer.shipping_policy")}</Link></li>
              <li><Link to="/legal/returns" className="hover:text-white">{t("footer.returns")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between text-xs text-white/50 gap-2">
          <p>© {new Date().getFullYear()} Preshh. All rights reserved.</p>
          <p>Preshh technology · Portugal</p>
        </div>
      </footer>
    </div>
  );
}
